import { Layers, Compass, Eye, Lightbulb, Rocket } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import { useAdmin } from '../lib/AdminContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { supabase } from '../lib/supabaseClient';
import { logUserEvent, getSongById } from '../lib/dataClient';
import { transformSongToCard } from '../lib/dataTransformers';
import { MOCK_CARDS } from '../lib/mockData';
import { useCivicInteractions } from '../hooks/useCivicInteractions';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useCivicXP } from '../hooks/useCivicXP';
import { useProfileData } from '../hooks/useProfileData';
import { useMomentumCelebrations } from '../hooks/useMomentumCelebrations';
import { getMicroActionByTitle, trackActiveEngagement } from '../lib/civicLevelService';
import { getSongActionModal, SongActionModal } from '../lib/actionModalService';
import { getPublicMediaUrl } from '../lib/mediaHelpers';
import { getModalContent, ModalBlock } from '../lib/supabase/modalContent';
import { getActionModalContent } from '../lib/actionModalContentService';
import type { ActionModalBlock } from '../lib/actionModalBlockTypes';
import { trackEvent } from '../lib/analytics';
import AuthRequiredModal from '../components/AuthRequiredModal';
import LevelUpModal from '../components/LevelUpModal';
import LookDeeperModalUnified from '../components/LookDeeperModalUnified';
import ExplorerLookDeeperModal from '../components/ExplorerLookDeeperModal';
import OneKeyInsightModalUnified from '../components/OneKeyInsightModalUnified';
import TakeItFurtherModalUnified from '../components/TakeItFurtherModalUnified';
import ExploreConnectionsModalUnified from '../components/ExploreConnectionsModalUnified';
import DynamicActionModal from '../components/DynamicActionModal';
import MomentumToast from '../components/MomentumToast';
import Tag from '../components/Tag';
import Button from '../components/Button';
import Header from '../components/Header';
import MoodChip from '../components/MoodChip';
import ListenNow from '../components/ListenNow';
import { CivicMood } from '../lib/types';
import type { CivicLevel } from '../lib/civicLevelTypes';
import { useState, useEffect, useRef } from 'react';

function getActionHintForLevel(levelName: string | null | undefined): string {
  switch (levelName) {
    case 'Observer':
      return 'Tap to take a small step.';
    case 'Explorer':
      return 'Tap to explore your next step.';
    case 'Sense-Maker':
      return 'Tap to connect the dots.';
    case 'Advocate':
      return 'Tap to put this into practice.';
    case 'Catalyst':
      return 'Tap to move the story forward.';
    case 'Change-Maker':
      return 'Tap to make your next impact.';
    default:
      return 'Tap to take a small step.';
  }
}

function getActionHeaderSubtitle(levelName: string | null | undefined): string {
  return 'Start anywhere. Every step counts.';
}

export default function CardDetail() {
  const {
    currentCardId,
    navigateTo,
    navigateReplacingCurrent,
    navigateBack,
    savedCardIds,
    toggleSaveCard,
    showActionWheel,
    setShowActionWheel,
    trackMood,
    getMoodsForCard,
    showToast,
    findRelatedStory,
  } = useApp();

  // ===== EVENT LOGGING & AUTH =====
  const effectiveUserData = useEffectiveUser();
  const user = effectiveUserData.user;
  const effectiveProfile = effectiveUserData.profile;
  const adminContext = useAdmin();
  const isAdmin = adminContext.isAdmin;
  const viewAsUser = adminContext.viewAsUser;
  const interactions = useCivicInteractions();
  const { isGuest, showAuthModal, authModalMessage, requireAuth, closeAuthModal } = useRequireAuth();
  const civicXP = useCivicXP(user?.id || '');
  const profileData = useProfileData(user?.id || null);
  const momentumCelebrations = useMomentumCelebrations();
  // ===== END EVENT LOGGING & AUTH =====

  const [showReflection, setShowReflection] = useState(false);
  const [reflectionResponse, setReflectionResponse] = useState<string | null>(null);
  const [showAffirmation, setShowAffirmation] = useState(false);
  const [affirmationMessage, setAffirmationMessage] = useState('');
  const [showLookDeeper, setShowLookDeeper] = useState(false);
  const [showQuickFact, setShowQuickFact] = useState(false);
  const [showRelatedSongs, setShowRelatedSongs] = useState(false);
  const [showTakeItFurther, setShowTakeItFurther] = useState(false);
  const [songData, setSongData] = useState<any>(null);
  const [actionModal, setActionModal] = useState<SongActionModal | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [lookDeeperBlocks, setLookDeeperBlocks] = useState<ModalBlock[]>([]);
  const [lookDeeperNewBlocks, setLookDeeperNewBlocks] = useState<ActionModalBlock[]>([]);
  const [lookDeeperSystem, setLookDeeperSystem] = useState<'legacy' | 'new'>('legacy');
  const [exploreConnectionsBlocks, setExploreConnectionsBlocks] = useState<ModalBlock[]>([]);
  const [exploreConnectionsNewBlocks, setExploreConnectionsNewBlocks] = useState<ActionModalBlock[]>([]);
  const [exploreConnectionsSystem, setExploreConnectionsSystem] = useState<'legacy' | 'new'>('legacy');
  const [oneKeyInsightNewBlocks, setOneKeyInsightNewBlocks] = useState<ActionModalBlock[]>([]);
  const [oneKeyInsightSystem, setOneKeyInsightSystem] = useState<'legacy' | 'new'>('legacy');
  const [takeItFurtherNewBlocks, setTakeItFurtherNewBlocks] = useState<ActionModalBlock[]>([]);
  const [takeItFurtherSystem, setTakeItFurtherSystem] = useState<'legacy' | 'new'>('legacy');

  // ===== SUPABASE MIGRATION: Try to get card from context first, fallback to MOCK_CARDS =====
  const allCards = useApp().getCards();
  const card = allCards.find(c => c.id === currentCardId) || MOCK_CARDS.find(c => c.id === currentCardId);
  // ===== END SUPABASE MIGRATION =====

  // Load song data from Supabase
  useEffect(() => {
    if (currentCardId || card?.id) {
      loadSongData();
    }
  }, [currentCardId, card?.id]);

  async function loadSongData() {
    const songId = currentCardId || card?.id;
    if (!songId) return;

    try {
      const song = await getSongById(songId);
      setSongData(song);
    } catch (err) {
      console.error('[CardDetail] Failed to load song data:', err);
    }
  }


  // Helper to get effective data (prioritize songData over card)
  const effectiveSongData = {
    id: songData?.id || card?.id || '',
    title: songData?.title || card?.songTitle || '',
    artist: songData?.artist || card?.artistName || '',
    image_url: getPublicMediaUrl(songData?.image_url || card?.albumArtUrl || ''),
    spotify_url: songData?.spotify_url || card?.spotifyUrl || '',
    apple_music_url: songData?.apple_music_url || card?.appleMusicUrl || '',
    youtube_url: songData?.youtube_url || card?.youtubeUrl || '',
    description: songData?.description || card?.shortDescription || '',
    lyric_spotlight: songData?.lyric_spotlight || card?.lyricHook || '',
    story_body: songData?.story_body || card?.storyText || '',
    deep_insight: songData?.deep_insight || card?.deepInsight || '',
    quick_fact: songData?.quick_fact || card?.quickFact || '',
    context_in_time: songData?.context_in_time || card?.contextInTime || '',
    related_song_ids: songData?.related_song_ids || [],
    // Quiz data for Observer level
    one_key_insight_question: songData?.one_key_insight_question || '',
    one_key_insight_option_a: songData?.one_key_insight_option_a || '',
    one_key_insight_option_b: songData?.one_key_insight_option_b || '',
    one_key_insight_option_c: songData?.one_key_insight_option_c || '',
    one_key_insight_correct_answer: songData?.one_key_insight_correct_answer || 'a',
    one_key_insight_explanation: songData?.one_key_insight_explanation || songData?.quick_fact || '',
  };

  // Load action modal based on effective civic level
  useEffect(() => {
    if (card?.id && effectiveProfile) {
      loadActionModal();
    }
  }, [card?.id, effectiveProfile?.civic_level]);

  async function loadActionModal() {
    if (!card?.id) return;

    setLoadingModal(true);
    try {
      const civicLevelKey = effectiveProfile?.civic_level?.toLowerCase().replace('-', '-') || null;
      const modal = await getSongActionModal(card.id, civicLevelKey);
      setActionModal(modal);
    } catch (err) {
      console.error('[CardDetail] Failed to load action modal:', err);
      setActionModal(null);
    } finally {
      setLoadingModal(false);
    }
  }

  // Reload modal content when civic level changes (for preview mode)
  // Admins in preview mode see drafts automatically; normal users see published only
  useEffect(() => {
    const songId = currentCardId || card?.id;
    const civicLevel = effectiveProfile?.civic_level || 'Observer';
    const includeDrafts = isAdmin && viewAsUser;

    if (!songId || !civicLevel) return;

    // Reload Look Deeper content if modal is open
    if (showLookDeeper) {
      (async () => {
        const result = await getActionModalContent(songId, civicLevel, 'look_deeper', includeDrafts);
        if (result.system === 'legacy') {
          setLookDeeperSystem('legacy');
          setLookDeeperBlocks(result.blocks);
          setLookDeeperNewBlocks([]);
        } else {
          setLookDeeperSystem('new');
          setLookDeeperBlocks([]);
          setLookDeeperNewBlocks(result.blocks);
        }
      })();
    }

    // Reload One Key Insight content if modal is open
    if (showQuickFact) {
      (async () => {
        const result = await getActionModalContent(songId, civicLevel, 'one_key_insight', includeDrafts);
        if (result.system === 'legacy') {
          setOneKeyInsightSystem('legacy');
          setOneKeyInsightNewBlocks([]);
        } else {
          setOneKeyInsightSystem('new');
          setOneKeyInsightNewBlocks(result.blocks);
        }
      })();
    }

    // Reload Explore Connections content if modal is open
    if (showRelatedSongs) {
      (async () => {
        const result = await getActionModalContent(songId, civicLevel, 'explore_connections', includeDrafts);
        if (result.system === 'legacy') {
          setExploreConnectionsSystem('legacy');
          setExploreConnectionsBlocks(result.blocks);
          setExploreConnectionsNewBlocks([]);
        } else {
          setExploreConnectionsSystem('new');
          setExploreConnectionsBlocks([]);
          setExploreConnectionsNewBlocks(result.blocks);
        }
      })();
    }

    // Reload Take It Further content if modal is open
    if (showTakeItFurther) {
      (async () => {
        const result = await getActionModalContent(songId, civicLevel, 'take_it_further', includeDrafts);
        if (result.system === 'legacy') {
          setTakeItFurtherSystem('legacy');
          setTakeItFurtherNewBlocks([]);
        } else {
          setTakeItFurtherSystem('new');
          setTakeItFurtherNewBlocks(result.blocks);
        }
      })();
    }
  }, [effectiveProfile?.civic_level, showLookDeeper, showQuickFact, showRelatedSongs, showTakeItFurther, isAdmin, viewAsUser]);

  // Prevent duplicate tracking on re-renders
  const hasTrackedView = useRef<string | null>(null);

  // Log card view event on mount
  useEffect(() => {
    if (card && user) {
      // Create unique key for this card view
      const viewKey = `${user.id}-${card.id}`;

      // Skip if we've already tracked this view
      if (hasTrackedView.current === viewKey) {
        return;
      }

      hasTrackedView.current = viewKey;

      // Track song explore for analytics
      trackEvent({
        event_name: 'song_explored',
        song_id: card.id,
      });

      // Track with new interaction system
      const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];
      interactions.trackStoryOpened({
        storyId: card.id,
        issueTags,
      });

      // Event tracking handled by trackStoryOpened above
    }
  }, [card?.id, user?.id]);

  // Helper function to handle save action (only if logged in)
  const executeSaveAction = async (issueTags: string[]) => {
    if (!user) return; // Safety check

    const wasSaved = isSaved;
    toggleSaveCard(card.id);

    // Track save/unsave with new system
    if (!wasSaved) {
      interactions.saveStory({ storyId: card.id, issueTags });

      // Award CP for saving (handled by saveStory in interactions hook)
      // No need to call awardCPForAction here - it's automatic in saveStory
    } else {
      interactions.unsaveStory(card.id);
    }

    // Event tracking handled by saveStory/unsaveStory above

    showToast(wasSaved ? 'Removed from collection' : 'Added to collection');
    setShowActionWheel(false);
  };

  // Helper function to award CP for active engagement
  async function awardCPForAction(interactionType: 'save_story' | 'look_deeper' | 'quick_fact' | 'explore_connections' | 'take_it_further') {
    if (!user || !card) return;

    try {
      const result = await trackActiveEngagement(user.id, card.id, interactionType);

      if (result?.cpAwarded && result.cpAwarded > 0) {
        console.log(`[CardDetail] Awarded ${result.cpAwarded} CP for ${interactionType}`);

        // Check for level up
        if (result.leveledUp && result.newLevel) {
          console.log(`[CardDetail] User leveled up to ${result.newLevel}!`);
        }
      } else if (result?.reason) {
        console.log(`[CardDetail] No CP awarded: ${result.reason}`);
      }
    } catch (err) {
      console.error('[CardDetail] Exception awarding CP:', err);
    }
  }

  // Handle "Look a little deeper" action
  async function handleLookDeeper() {
    if (isGuest) {
      requireAuth('Sign in to explore deeper insights');
      return;
    }

    // Load modal blocks based on user's civic level
    // Admins in preview mode see drafts automatically
    const songId = currentCardId || card?.id;
    const civicLevel = effectiveProfile?.civic_level || 'Observer';
    const includeDrafts = isAdmin && viewAsUser;

    if (songId && civicLevel) {
      const result = await getActionModalContent(songId, civicLevel, 'look_deeper', includeDrafts);

      if (result.system === 'legacy') {
        setLookDeeperSystem('legacy');
        setLookDeeperBlocks(result.blocks);
        setLookDeeperNewBlocks([]);
      } else {
        setLookDeeperSystem('new');
        setLookDeeperBlocks([]);
        setLookDeeperNewBlocks(result.blocks);
      }
    }

    // Track modal open for analytics
    trackEvent({
      event_name: 'action_modal_opened',
      song_id: card.id,
      action_modal: 'look_deeper',
    });

    setShowLookDeeper(true);
    setShowActionWheel(false);

    if (user) {
      await awardCPForAction('look_deeper');
    }
  }

  // Handle "One quick fact" action
  async function handleQuickFact() {
    if (isGuest) {
      requireAuth('Sign in to see quick facts');
      return;
    }

    // Load modal content based on user's civic level
    // Admins in preview mode see drafts automatically
    const songId = currentCardId || card?.id;
    const civicLevel = effectiveProfile?.civic_level || 'Observer';
    const includeDrafts = isAdmin && viewAsUser;

    if (songId && civicLevel) {
      const result = await getActionModalContent(songId, civicLevel, 'one_key_insight', includeDrafts);

      if (result.system === 'new') {
        setOneKeyInsightSystem('new');
        setOneKeyInsightNewBlocks(result.blocks);
      } else {
        setOneKeyInsightSystem('legacy');
        setOneKeyInsightNewBlocks([]);
      }
    }

    // Track modal open for analytics
    trackEvent({
      event_name: 'action_modal_opened',
      song_id: card.id,
      action_modal: 'one_key_insight',
    });

    setShowQuickFact(true);
    setShowActionWheel(false);

    if (user) {
      await awardCPForAction('quick_fact');
    }
  }

  // Handle "Explore related songs" action
  async function handleExploreRelated() {
    if (isGuest) {
      requireAuth('Sign in to explore related songs');
      return;
    }

    // Load modal blocks based on user's civic level
    // Admins in preview mode see drafts automatically
    const songId = currentCardId || card?.id;
    const civicLevel = effectiveProfile?.civic_level || 'Observer';
    const includeDrafts = isAdmin && viewAsUser;

    if (songId && civicLevel) {
      const result = await getActionModalContent(songId, civicLevel, 'explore_connections', includeDrafts);

      if (result.system === 'legacy') {
        setExploreConnectionsSystem('legacy');
        setExploreConnectionsBlocks(result.blocks);
        setExploreConnectionsNewBlocks([]);
      } else {
        setExploreConnectionsSystem('new');
        setExploreConnectionsBlocks([]);
        setExploreConnectionsNewBlocks(result.blocks);
      }
    }

    // Track modal open for analytics
    trackEvent({
      event_name: 'action_modal_opened',
      song_id: card.id,
      action_modal: 'explore_connections',
    });

    setShowRelatedSongs(true);
    setShowActionWheel(false);

    if (user) {
      await awardCPForAction('explore_connections');
    }
  }

  // Handle "Take it further" action
  async function handleTakeItFurther() {
    if (isGuest) {
      requireAuth('Sign in to take it further');
      return;
    }

    // Load modal content based on user's civic level
    // Admins in preview mode see drafts automatically
    const songId = currentCardId || card?.id;
    const civicLevel = effectiveProfile?.civic_level || 'Observer';
    const includeDrafts = isAdmin && viewAsUser;

    if (songId && civicLevel) {
      const result = await getActionModalContent(songId, civicLevel, 'take_it_further', includeDrafts);

      if (result.system === 'new') {
        setTakeItFurtherSystem('new');
        setTakeItFurtherNewBlocks(result.blocks);
      } else {
        setTakeItFurtherSystem('legacy');
        setTakeItFurtherNewBlocks([]);
      }
    }

    // Track modal open for analytics
    trackEvent({
      event_name: 'action_modal_opened',
      song_id: card.id,
      action_modal: 'take_it_further',
    });

    setShowTakeItFurther(true);
    setShowActionWheel(false);

    if (user) {
      await awardCPForAction('take_it_further');
    }
  }

  // Handle selecting a related song
  function handleSelectRelatedSong(songId: string) {
    navigateReplacingCurrent('card-detail', songId);
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <p className="text-[#A0A0A0]">Card not found</p>
      </div>
    );
  }

  const isSaved = savedCardIds.includes(card.id);

  // Debug logging with full array contents
  console.log('[CardDetail] Checking if saved:', {
    cardId: card.id,
    cardTitle: card.title,
    savedCardIdsArray: JSON.stringify(savedCardIds),
    savedCount: savedCardIds.length,
    isSaved: isSaved
  });

  const handleAction = (action: string) => {
    const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];

    // Guard save action for guests
    if (action === 'save') {
      requireAuth(() => executeSaveAction(issueTags), "Save stories and track them over time.");
      setShowActionWheel(false);
      return;
    }

    // Guard reflect action for guests
    if (action === 'reflect') {
      requireAuth(() => {
        setShowReflection(true);
        setShowActionWheel(false);
      }, "Save your reflections and see patterns.");
      setShowActionWheel(false);
      return;
    }

    // Track micro-action with new system (logged-in only)
    if (user) {
      interactions.trackMicroAction({
        storyId: card.id,
        microActionKey: action,
        issueTags,
      });

      // Event tracking handled by trackMicroAction above
    }

    if (action === 'find-more') {
      setShowActionWheel(false);
      const relatedStory = findRelatedStory(card.id);
      if (relatedStory) {
        navigateTo('card-detail', relatedStory.id);
        showToast('Showing another perspective');
      } else {
        navigateTo('home');
        showToast('No related stories found');
      }
    } else if (action === 'roots') {
      if (card.timeline && card.timeline.length > 0) {
        // Track timeline opened
        if (user) {
          interactions.trackTimelineOpened({ storyId: card.id, issueTags });
        }
        setShowTimeline(true);
        setShowActionWheel(false);
        showToast('Viewing story timeline');
      } else {
        showToast('No timeline available for this story');
      }
    } else if (action === 'backstory') {
      // Track backstory opened
      if (user) {
        interactions.trackBackstoryOpened({ storyId: card.id, issueTags });
      }
      showToast('Backstory feature coming soon');
    } else if (action === 'share') {
      showToast('Share feature coming soon');
    }
  };

  const handleReflectionResponse = (response: string) => {
    setReflectionResponse(response);

    // Event tracking handled by micro-action tracking above

    const affirmations = [
      'Thank you for reflecting.',
      'Your civic journey grows one moment at a time.',
      'No right answers—only honest ones.'
    ];
    const randomAffirmation = affirmations[Math.floor(Math.random() * affirmations.length)];

    setTimeout(() => {
      setShowReflection(false);
      setReflectionResponse(null);
      setAffirmationMessage(randomAffirmation);
      setShowAffirmation(true);

      setTimeout(() => {
        setShowAffirmation(false);
      }, 2000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <Header
        mode="title"
        title=""
        showBackButton={true}
        backButtonLabel="Back"
        onBackClick={navigateBack}
      />

      <div className="relative w-full flex justify-center pb-2 min-h-screen">
        <div
          className="fixed top-0 left-0 w-screen h-screen"
          style={{
            background: '#111111',
          }}
        />

        <div className="relative w-full max-w-[880px] mx-auto px-4 md:px-8 pt-4 pb-24 z-10">
          {/* Song Detail Header Card */}
          <div
            className="card-elevated-hero bg-[#1A1A1A] rounded-xl border relative overflow-hidden"
            style={{
              borderColor: 'rgba(64, 224, 208, 0.55)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.45), 0px 2px 4px rgba(0, 0, 0, 0.55), inset 0px 1px 3px rgba(0, 0, 0, 0.12), inset 0px 0px 1px rgba(255,255,255,0.03)',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'%3E%3Cfilter id=\'n\' x=\'0\' y=\'0\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'3.2\' numOctaves=\'2\' /%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'200\' height=\'200\' filter=\'url(%23n)\' opacity=\'0.012\'/%3E%3C/svg%3E")',
              backgroundBlendMode: 'soft-light'
            }}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{
                backgroundColor: 'rgba(64, 224, 208, 0.60)',
                boxShadow: 'inset 0px -1.5px 2px rgba(0, 0, 0, 0.07)'
              }}
            />

            {/* Desktop Layout: Album + Text column on left, Collect floats top-right */}
            <div className="hidden md:block p-6">
              <div className="flex gap-5 items-start">
                {/* Album Art with ambient glow */}
                <div className="relative flex-shrink-0" style={{ zIndex: 1 }}>
                  <div
                    className="absolute inset-0 rounded-lg"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(64, 224, 208, 0.25) 0%, rgba(64, 224, 208, 0.12) 40%, transparent 70%)',
                      filter: 'blur(16px)',
                      transform: 'scale(1.15)',
                      zIndex: -1,
                    }}
                  />
                  <img
                    src={effectiveSongData.image_url}
                    alt={effectiveSongData.title}
                    className="relative w-32 h-32 rounded-lg object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
                    }}
                  />
                </div>

                {/* Text Column - takes remaining space */}
                <div className="relative flex-1 min-w-0 pt-1" style={{ zIndex: 2 }}>
                  <h1
                    className="text-[22px] font-semibold text-white line-clamp-2 pr-32"
                    style={{ letterSpacing: '-0.005em' }}
                  >
                    {card.songTitle}
                  </h1>
                  <p className="text-[14px] text-[#A0A0A0] mt-1">
                    {card.artistName}
                  </p>
                  <div className="flex gap-2 flex-wrap mt-3.5">
                    <Tag variant="chip" small>
                      {card.primaryIssueTag}
                    </Tag>
                    {card.secondaryIssueTags.map(tag => (
                      <Tag key={tag} variant="chip" small>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>

                {/* Collect Button - Positioned independently in top-right */}
                <div className="absolute top-6 right-6" style={{ zIndex: 10 }}>
                  <button
                    onClick={() => {
                      const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];
                      requireAuth(() => executeSaveAction(issueTags), "Collect stories and track them over time.");
                    }}
                    className="flex items-center gap-2 px-4 rounded-full h-[42px] min-h-[44px] border transition-all duration-200 active:scale-95 flex-shrink-0"
                    style={{
                      backgroundColor: isSaved ? 'rgba(64, 224, 208, 0.12)' : 'transparent',
                      borderColor: isSaved ? 'rgba(64, 224, 208, 0.85)' : 'rgba(64, 224, 208, 0.50)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSaved) {
                        e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSaved) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    aria-label={isSaved ? 'Remove from collection' : 'Collect this story'}
                  >
                    <Layers
                      className="w-4 h-4"
                      stroke="#40E0D0"
                      strokeWidth={1.5}
                      fill={isSaved ? '#40E0D0' : 'none'}
                    />
                    <span
                      className="text-[14px] font-medium"
                      style={{ color: '#40E0D0' }}
                    >
                      {isSaved ? 'Collected' : 'Collect'}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Layout: Album + Text row, then Collect button centered below */}
            <div className="md:hidden pt-4 px-4 pb-3">
              {/* Row 1: Album Art + Text Column */}
              <div className="flex gap-3 items-start">
                {/* Album Art with ambient glow */}
                <div className="relative flex-shrink-0" style={{ zIndex: 1 }}>
                  <div
                    className="absolute inset-0 rounded-lg"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(64, 224, 208, 0.25) 0%, rgba(64, 224, 208, 0.12) 40%, transparent 70%)',
                      filter: 'blur(14px)',
                      transform: 'scale(1.2)',
                      zIndex: -1,
                    }}
                  />
                  <img
                    src={effectiveSongData.image_url}
                    alt={effectiveSongData.title}
                    className="relative w-24 h-24 rounded-lg object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
                    }}
                  />
                </div>

                {/* Text Block */}
                <div className="relative flex-1 min-w-0 pt-0.5" style={{ zIndex: 2 }}>
                  <h1
                    className="text-[20px] font-semibold text-white line-clamp-2"
                    style={{ letterSpacing: '-0.005em' }}
                  >
                    {card.songTitle}
                  </h1>
                  <p className="text-[14px] text-[#A0A0A0] mt-1">
                    {card.artistName}
                  </p>
                  <div className="flex gap-2 flex-wrap mt-1.5">
                    <Tag variant="chip" small>
                      {card.primaryIssueTag}
                    </Tag>
                    {card.secondaryIssueTags.map(tag => (
                      <Tag key={tag} variant="chip" small>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>

              {/* Row 2: Centered Collect Button */}
              <div className="flex justify-center mt-3 pb-1">
                <button
                  onClick={() => {
                    const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];
                    requireAuth(() => executeSaveAction(issueTags), "Collect stories and track them over time.");
                  }}
                  className="flex items-center justify-center gap-2 px-4 rounded-full h-[42px] min-h-[44px] border transition-all duration-200 active:scale-95 w-[75%]"
                  style={{
                    backgroundColor: isSaved ? 'rgba(64, 224, 208, 0.12)' : 'transparent',
                    borderColor: isSaved ? 'rgba(64, 224, 208, 0.85)' : 'rgba(64, 224, 208, 0.50)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSaved) {
                      e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSaved) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  aria-label={isSaved ? 'Remove from collection' : 'Collect this story'}
                >
                  <Layers
                    className="w-4 h-4"
                    stroke="#40E0D0"
                    strokeWidth={1.5}
                    fill={isSaved ? '#40E0D0' : 'none'}
                  />
                  <span
                    className="text-[15px] font-medium"
                    style={{ color: '#40E0D0' }}
                  >
                    {isSaved ? 'Collected' : 'Collect'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {effectiveSongData.lyric_spotlight && (
            <div className="card-elevated mt-6 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <div className="text-[15px] font-semibold leading-[20px] mb-3" style={{ letterSpacing: '0.5px', color: '#40E0D0', opacity: 0.88 }}>
              Lyric Spotlight
            </div>
            <p className="lyric-text text-[16px] text-[#F5F5F5]" style={{ lineHeight: '1.55' }}>
              "{effectiveSongData.lyric_spotlight}"
              </p>
            </div>
          )}

          <div className="card-elevated mt-6 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
          <div className="text-[15px] font-semibold leading-[20px] mb-3" style={{ letterSpacing: '0.5px', color: '#40E0D0', opacity: 0.88 }}>
            The Story
          </div>

          <div className="text-[16px] text-[#F5F5F5] space-y-4" style={{ lineHeight: '1.55' }}>
            {card.storyContent.split('\n\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
            </div>
          </div>

          <div className="card-elevated mt-6 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <ListenNow songId={card.id} spotifyUrl={effectiveSongData.spotify_url} appleMusicUrl={effectiveSongData.apple_music_url} youtubeUrl={effectiveSongData.youtube_url} />
          </div>

          {card.visualUrl && (
            <div className="card-elevated mt-8 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <img
              src={card.visualUrl}
              alt={card.visualCaption}
              className="w-full rounded-lg"
            />
            {card.visualCaption && (
              <p className="text-[12px] text-[#A0A0A0] italic mt-2">
                {card.visualCaption}
                </p>
              )}
            </div>
          )}

          <div className="card-elevated mt-6 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
          <div className="text-[15px] font-semibold leading-[20px] mb-3" style={{ letterSpacing: '0.5px', color: '#40E0D0', opacity: 0.88 }}>
            How Did This Story Make You Feel?
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap [&>*]:w-full md:[&>*]:w-auto">
            {(['Moved', 'Empowered', 'Curious', 'Heavy'] as CivicMood[]).map(mood => {
              const selectedMoods = getMoodsForCard(card.id);
              return (
                <MoodChip
                  key={mood}
                  mood={mood}
                  selected={selectedMoods.includes(mood)}
                  onClick={() => {
                    requireAuth(() => {
                      trackMood(card.id, mood, card.songTitle, card.artistName);

                      // Track reaction for analytics
                      trackEvent({
                        event_name: 'reaction_selected',
                        song_id: card.id,
                        reaction: mood,
                      });

                      if (user) {
                        const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];

                        // Track emotion click - this handles user_feelings insert, user_interactions, and CP award
                        interactions.trackEmotionClick(card.id, mood, issueTags);
                      }
                    }, "Track your feelings and see patterns.");
                  }}
                  cardId={card.id}
                />
              );
              })}
            </div>
          </div>


          <div className="card-elevated mt-8 bg-[#1A1A1A] rounded-xl border relative overflow-hidden p-4 md:p-5 mb-4" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent pointer-events-none" />
            <p className="text-[12px] text-[#A0A0A0] text-center mb-3">
              {getActionHintForLevel(effectiveProfile?.civic_level)}
            </p>
            <Button
              fullWidth
              variant="primary"
              onClick={() => setShowActionWheel(true)}
              className="focus:!ring-0 focus:!ring-offset-0 focus:outline-none"
            >
              What Can I Do With This?
            </Button>
          </div>
        </div>
      </div>

      {showActionWheel && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{
            '--bottom-nav-offset': 'calc(72px + env(safe-area-inset-bottom))',
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'var(--bottom-nav-offset)',
          } as React.CSSProperties}
          onClick={() => setShowActionWheel(false)}
        >
          <div
            className="relative w-full max-w-[840px] bg-[#141414] rounded-xl border"
            style={{
              borderColor: 'rgba(64, 224, 208, 0.55)',
              boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowActionWheel(false)}
              className="absolute top-4 right-4 p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors z-10"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#808080">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="px-8 pt-7 pb-2">
              <h3
                className="text-[18px] font-semibold text-[#F5F5F5] mb-1"
                style={{ letterSpacing: '-0.005em' }}
              >
                What resonates?
              </h3>
              <p className="text-[13px] text-[#A0A0A0]">
                {getActionHeaderSubtitle(effectiveProfile?.civic_level)}
              </p>
            </div>

            <div className="px-8 pt-3 pb-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[800px] mx-auto">
                <button
                  onClick={handleLookDeeper}
                  className="w-full text-left bg-[#1A1A1A] rounded-xl pl-6 md:pl-4 pr-4 md:pr-3 py-6 border focus-visible:outline-none"
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                    transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="flex items-center gap-4 md:gap-2.5">
                    <div
                      data-icon-container
                      className="w-12 md:w-10 h-12 md:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(64, 224, 208, 0.12)',
                        transition: 'background-color 140ms ease-out, box-shadow 140ms ease-out',
                      }}
                    >
                      <Eye className="w-7 md:w-6 h-7 md:h-6" style={{ color: '#40E0D0' }} />
                    </div>
                    <div className="flex-1 min-w-0 md:max-w-[260px]">
                      <h4 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                        Look Deeper
                      </h4>
                      <p className="text-[12px] text-[#A0A0A0] leading-relaxed">
                        The story, the context, the why.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleQuickFact}
                  className="w-full text-left bg-[#1A1A1A] rounded-xl pl-6 md:pl-4 pr-4 md:pr-3 py-6 border focus-visible:outline-none"
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                    transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="flex items-center gap-4 md:gap-2.5">
                    <div
                      data-icon-container
                      className="w-12 md:w-10 h-12 md:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(64, 224, 208, 0.12)',
                        transition: 'background-color 140ms ease-out, box-shadow 140ms ease-out',
                      }}
                    >
                      <Lightbulb className="w-7 md:w-6 h-7 md:h-6" style={{ color: '#40E0D0' }} />
                    </div>
                    <div className="flex-1 min-w-0 md:max-w-[260px]">
                      <h4 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                        One Key Insight
                      </h4>
                      <p className="text-[12px] text-[#A0A0A0] leading-relaxed">
                        One shift in perspective.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleExploreRelated}
                  className="w-full text-left bg-[#1A1A1A] rounded-xl pl-6 md:pl-4 pr-4 md:pr-3 py-6 border focus-visible:outline-none"
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                    transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="flex items-center gap-4 md:gap-2.5">
                    <div
                      data-icon-container
                      className="w-12 md:w-10 h-12 md:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(64, 224, 208, 0.12)',
                        transition: 'background-color 140ms ease-out, box-shadow 140ms ease-out',
                      }}
                    >
                      <Compass className="w-7 md:w-6 h-7 md:h-6" style={{ color: '#40E0D0' }} />
                    </div>
                    <div className="flex-1 min-w-0 md:max-w-[260px]">
                      <h4 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                        Explore Connections
                      </h4>
                      <p className="text-[12px] text-[#A0A0A0] leading-relaxed">
                        Related stories and themes.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleTakeItFurther}
                  className="w-full text-left bg-[#1A1A1A] rounded-xl pl-6 md:pl-4 pr-4 md:pr-3 py-6 border focus-visible:outline-none"
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
                    transition: 'border-color 140ms ease-out, box-shadow 140ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.32)';
                    e.currentTarget.style.boxShadow = '0px 8px 20px rgba(0, 0, 0, 0.48), 0px 2px 6px rgba(0, 0, 0, 0.28), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.18)';
                      iconContainer.style.boxShadow = 'inset 0px 0px 12px rgba(64, 224, 208, 0.26)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.boxShadow = '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)';
                    const iconContainer = e.currentTarget.querySelector('[data-icon-container]') as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.backgroundColor = 'rgba(64, 224, 208, 0.12)';
                      iconContainer.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div className="flex items-center gap-4 md:gap-2.5">
                    <div
                      data-icon-container
                      className="w-12 md:w-10 h-12 md:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(64, 224, 208, 0.12)',
                        transition: 'background-color 140ms ease-out, box-shadow 140ms ease-out',
                      }}
                    >
                      <Rocket className="w-6 md:w-5 h-6 md:h-5" style={{ color: '#40E0D0', transform: 'translateY(-0.5px)' }} />
                    </div>
                    <div className="flex-1 min-w-0 md:max-w-[260px]">
                      <h4 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                        Take It Further
                      </h4>
                      <p className="text-[12px] text-[#A0A0A0] leading-relaxed">
                        A small step you can choose.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showReflection && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center px-8"
          style={{
            background: 'radial-gradient(circle at center, rgba(20, 20, 20, 0.92) 0%, rgba(0, 0, 0, 0.85) 100%)',
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <div className="bg-[#1A1A1A] border rounded-xl p-6 w-full max-w-sm relative overflow-hidden" style={{ borderColor: 'rgba(255, 255, 255, 0.12)', boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.8)' }}>
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] via-transparent to-transparent pointer-events-none" />
            {reflectionResponse ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#40E0D0] bg-opacity-20 flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-[#40E0D0]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-[16px] text-[#F5F5F5]">Reflection saved</p>
              </div>
            ) : (
              <>
                <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2 text-center">
                  Did this shift how you see this?
                </h3>
                <p className="text-[14px] text-[#A0A0A0] mb-6 text-center">
                  Be honest—there's no right answer.
                </p>
                <div className="space-y-3">
                  <button
                    className="w-full h-[44px] px-6 bg-[#40E0D0] hover:bg-[#47EAD8] active:bg-[#40E0D0] text-[#031414] font-semibold text-[15px] rounded-full inline-flex items-center justify-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
                    onClick={() => handleReflectionResponse('yes')}
                  >
                    Yes
                  </button>
                  <button
                    className="w-full h-[44px] px-6 border border-[#40E0D0] text-[#40E0D0] bg-transparent hover:bg-[#40E0D0]/10 active:bg-[#40E0D0]/20 font-medium text-[15px] rounded-full inline-flex items-center justify-center gap-2 transition-all duration-200"
                    onClick={() => handleReflectionResponse('not_sure')}
                  >
                    Not sure
                  </button>
                  <button
                    className="w-full h-[44px] px-6 border border-[#40E0D0] text-[#40E0D0] bg-transparent hover:bg-[#40E0D0]/10 active:bg-[#40E0D0]/20 font-medium text-[15px] rounded-full inline-flex items-center justify-center gap-2 transition-all duration-200"
                    onClick={() => handleReflectionResponse('no')}
                  >
                    No
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showAffirmation && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-6 animate-fade-in">
          <div className="bg-[#1A1A1A]/95 border backdrop-blur-sm rounded-xl px-6 py-4 max-w-sm relative overflow-hidden" style={{ borderColor: 'rgba(255, 255, 255, 0.1)', boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.7)' }}>
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.025] via-transparent to-transparent pointer-events-none" />
            <p className="text-[14px] text-[#E0E0E0] text-center leading-relaxed">
              {affirmationMessage}
            </p>
          </div>
        </div>
      )}

      <AuthRequiredModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        message={authModalMessage}
      />

      <LevelUpModal
        isOpen={civicXP.showLevelUpModal}
        onClose={civicXP.closeLevelUpModal}
        newLevel={(civicXP.levelUpData?.newLevel || 'Observer') as CivicLevel}
        badge={civicXP.levelUpData?.badge || null}
      />

      <MomentumToast
        message={momentumCelebrations.momentumMessage}
        isVisible={momentumCelebrations.showMomentumToast}
        onDismiss={momentumCelebrations.dismissMomentumToast}
      />

      {showLookDeeper && (effectiveProfile?.civic_level || 'Observer') === 'Explorer' ? (
        <ExplorerLookDeeperModal
          songId={card?.id || currentCardId || ''}
          songTitle={effectiveSongData.title}
          onClose={() => setShowLookDeeper(false)}
        />
      ) : showLookDeeper ? (
        <LookDeeperModalUnified
          isOpen={showLookDeeper}
          onClose={() => setShowLookDeeper(false)}
          system={lookDeeperSystem}
          legacyBlocks={lookDeeperBlocks}
          newBlocks={lookDeeperNewBlocks}
          songTitle={effectiveSongData.title}
          civicLevel={effectiveProfile?.civic_level || 'Observer'}
          songId={card?.id || currentCardId}
        />
      ) : null}

      <OneKeyInsightModalUnified
        isOpen={showQuickFact}
        onClose={() => setShowQuickFact(false)}
        system={oneKeyInsightSystem}
        legacyQuickFact={effectiveSongData.quick_fact}
        quizData={
          effectiveSongData.one_key_insight_question && effectiveSongData.one_key_insight_option_a
            ? {
                question: effectiveSongData.one_key_insight_question,
                option_a: effectiveSongData.one_key_insight_option_a,
                option_b: effectiveSongData.one_key_insight_option_b,
                option_c: effectiveSongData.one_key_insight_option_c,
                correct_answer: effectiveSongData.one_key_insight_correct_answer as 'a' | 'b' | 'c',
                explanation: effectiveSongData.one_key_insight_explanation,
              }
            : undefined
        }
        newBlocks={oneKeyInsightNewBlocks}
        songTitle={effectiveSongData.title}
        songId={effectiveSongData.id}
        civicLevel={effectiveProfile?.civic_level || 'Observer'}
      />

      <ExploreConnectionsModalUnified
        isOpen={showRelatedSongs}
        onClose={() => setShowRelatedSongs(false)}
        system={exploreConnectionsSystem}
        currentSongId={card.id}
        onSelectSong={handleSelectRelatedSong}
        newBlocks={exploreConnectionsNewBlocks}
        civicLevel={effectiveProfile?.civic_level || 'Observer'}
      />

      <TakeItFurtherModalUnified
        isOpen={showTakeItFurther && !actionModal}
        onClose={() => setShowTakeItFurther(false)}
        system={takeItFurtherSystem}
        onSaveStory={() => {
          const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];
          if (user) {
            executeSaveAction(issueTags);
          }
        }}
        isStorySaved={isSaved}
        newBlocks={takeItFurtherNewBlocks}
        civicLevel={effectiveProfile?.civic_level || 'Observer'}
        songId={card?.id || currentCardId}
      />

      <DynamicActionModal
        isOpen={showTakeItFurther && !!actionModal}
        onClose={() => setShowTakeItFurther(false)}
        modal={actionModal}
        onCtaClick={() => {
          const issueTags = [card.primaryIssueTag, ...card.secondaryIssueTags];
          if (user) {
            executeSaveAction(issueTags);
          }
        }}
      />
    </div>
  );
}
